import React from 'react'
import PropTypes from 'prop-types'
import '../assets/css/spinners.css'
import { mainUILoad } from '../lib/uiHelper'

/**
 * Loading component displays a spinner based on application settings.
 * It initializes the main UI settings using environment data and displays a spinner
 * as specified in the parsed application data.
 *
 * @module common/Loading
 * @description Displays a loading spinner with styles based on application settings.
 *
 * @component
 * @example
 * return <Loading envData={envConfig} appDataParsed={appConfig} />
 *
 * @param {Object} envData - Environment configuration data.
 * @param {Object} [appDataParsed] - Parsed application data including spinner settings.
 *
 * @returns {React.Element} Renders a loading spinner based on the application settings.
 */
export const Loading = ({ envData, appDataParsed }) => {
  // Initialize UI settings based on environment data
  mainUILoad(
    envData.REACT_APP_PRIMARY_COLOR,
    envData.REACT_APP_SECONDARY_COLOR,
    envData.REACT_APP_THEME_FAVICON,
  )

  // Available spinner types
  const loader = {
    dualRing: 'lds-dual-ring',
    circle: 'lds-circle',
    facebook: 'lds-facebook',
    heart: 'lds-heart',
    ring: 'lds-ring',
    roller: 'lds-roller',
    default: 'lds-default',
    ellipsis: 'lds-ellipsis',
    grid: 'lds-grid',
    hourglass: 'lds-hourglass',
    ripple: 'lds-ripple',
    spinner: 'lds-spinner',
  }

  return (
    <div className="custom-spinner" data-testid="loading-Spinner">
      <div className="custom-spinner-container">
        <div
          className={
            appDataParsed
              ? appDataParsed.webSettings['webSettings-defaultSpinner']
              : loader.dualRing
          }
        ></div>
      </div>
    </div>
  )
}

Loading.propTypes = {
  /**
   * Environment configuration data used to initialize the UI.
   */
  envData: PropTypes.shape({
    REACT_APP_PRIMARY_COLOR: PropTypes.string.isRequired,
    REACT_APP_SECONDARY_COLOR: PropTypes.string.isRequired,
    REACT_APP_THEME_FAVICON: PropTypes.string.isRequired,
  }).isRequired,

  /**
   * Parsed application data, including settings for the spinner.
   */
  appDataParsed: PropTypes.shape({
    webSettings: PropTypes.shape({
      'webSettings-defaultSpinner': PropTypes.string,
    }),
  }),
}
